import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import PhotoViewer from './PhotoViewer';
import { productImageUrl } from './AiLookResults';
import { formatProductPriceEur, selectedProductsTotalEur } from './currency';
import { setPageMeta } from './pageMeta';

export default function PublicTryOn() {
  const { shareId } = useParams();
  const { formatDate, locale, t } = useI18n();
  const [tryOn, setTryOn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photoViewer, setPhotoViewer] = useState({ open: false, sources: [], initialSlide: 0 });
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }),
    [locale],
  );

  useEffect(() => {
    setPageMeta({
      title: `${t('publicTryOn.kicker')} | Outfit Detective`,
      description: t('publicTryOn.ctaCopy'),
      pathname: `/t/${shareId}`,
      robots: 'noindex,follow',
    });
  }, [shareId, t]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.getPublicTryOn(shareId)
      .then((response) => {
        if (mounted) setTryOn(response.data || null);
      })
      .catch((err) => {
        if (mounted) setError(err.message);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [shareId]);

  function formatPrice(product) {
    return formatProductPriceEur(product, currencyFormatter, t('detail.priceUnavailable'));
  }

  function formatSelectedTotal() {
    const total = selectedProductsTotalEur(tryOn?.selected_products || []);
    if (total === null) return t('tryon.totalUnavailable');
    return currencyFormatter.format(total);
  }

  function celebrityPath() {
    return tryOn?.event?.celebrity_slug ? `/style/${tryOn.event.celebrity_slug}` : '/';
  }

  function sourcePostPath() {
    return tryOn?.event?.id ? `/events/${tryOn.event.id}` : celebrityPath();
  }

  function photoViewerSources() {
    return [tryOn?.image_url, tryOn?.event?.image_url].filter(Boolean);
  }

  function openPhotoViewer(initialSlide = 0) {
    const sources = photoViewerSources();
    if (!sources.length) return;
    setPhotoViewer({ open: true, sources, initialSlide: Math.min(Math.max(initialSlide, 0), sources.length - 1) });
  }

  useEffect(() => {
    if (!tryOn) return;
    const celebrity = tryOn.event?.celebrity_name || t('tryonShare.celebrityFallback');
    const creator = tryOn.sharer?.name || t('tryonFeed.creatorFallback');
    const selectedTotal = selectedProductsTotalEur(tryOn.selected_products || []);
    const totalLabel = selectedTotal === null ? '' : currencyFormatter.format(selectedTotal);
    setPageMeta({
      title: `${t('publicTryOn.title', { celebrity })} | Outfit Detective`,
      description: tryOn.try_on_type === 'selected_products' && totalLabel
        ? t('publicTryOn.shopCopy', { creator, total: totalLabel })
        : t('publicTryOn.copy', { creator }),
      pathname: `/t/${shareId}`,
      image: tryOn.image_url,
      robots: 'noindex,follow',
    });
  }, [tryOn, shareId, t, currencyFormatter]);

  if (loading) {
    return (
      <main className="ct-public-tryon-page">
        <div className="ct-card ct-empty">{t('common.loading')}</div>
      </main>
    );
  }

  if (error || !tryOn) {
    return (
      <main className="ct-public-tryon-page">
        <section className="ct-public-tryon-missing">
          <img src="/brand/outfit-detective-mark-transparent.svg" alt="" />
          <h1>{t('publicTryOn.missingTitle')}</h1>
          <p>{t('publicTryOn.missingCopy')}</p>
          <Link to="/" className="btn btn-dark">{t('publicTryOn.cta')}</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="ct-public-tryon-page">
      <PhotoViewer
        open={photoViewer.open}
        sources={photoViewer.sources}
        initialSlide={photoViewer.initialSlide}
        onClose={() => setPhotoViewer({ open: false, sources: [], initialSlide: 0 })}
        t={t}
      />

      <section className="ct-public-tryon-shell ct-public-tryon-shell-detail">
        <article className="ct-user-fit-detail">
          <button type="button" className="ct-user-fit-media" onClick={() => openPhotoViewer(0)}>
            <img src={tryOn.image_url} alt={t('tryon.resultAlt')} />
          </button>

          <aside className="ct-user-fit-panel">
            <div className="ct-user-fit-author">
              <span className="ct-fit-wall-avatar">
                <i className="ph ph-sparkle"></i>
              </span>
              <div>
                <strong>{tryOn.sharer?.name || t('tryonFeed.creatorFallback')}</strong>
                <span>{formatDate(tryOn.completed_at || tryOn.created_at, { dateStyle: 'medium', timeStyle: 'short' })}</span>
              </div>
            </div>

            <div className="ct-user-fit-copy">
              <p className="ct-kicker">{t('publicTryOn.kicker')}</p>
              <h1>{t('publicTryOn.title', { celebrity: tryOn.event?.celebrity_name || t('tryonShare.celebrityFallback') })}</h1>
              <p>
                {tryOn.try_on_type === 'selected_products'
                  ? t('publicTryOn.shopCopy', { creator: tryOn.sharer?.name || t('tryonFeed.creatorFallback'), total: formatSelectedTotal() })
                  : t('publicTryOn.copy', { creator: tryOn.sharer?.name || t('tryonFeed.creatorFallback') })}
              </p>
            </div>

            {tryOn.event?.image_url ? (
              <button type="button" className="ct-user-fit-original" onClick={() => openPhotoViewer(1)}>
                <img src={tryOn.event.image_url} alt="" />
                <span>
                  <strong>{tryOn.event.title || t('tryonGallery.originalPost')}</strong>
                  <em>{tryOn.event.source_name || t('tryonGallery.originalPost')}</em>
                </span>
              </button>
            ) : null}

            <section className="ct-user-fit-cta">
              <div>
                <p className="ct-kicker">{t('publicTryOn.ctaKicker')}</p>
                <h2>{t('publicTryOn.ctaTitle')}</h2>
              </div>
              <p>{t('publicTryOn.ctaCopy')}</p>
              <div className="ct-public-tryon-cta-actions">
                <Link to={sourcePostPath()} className="btn btn-dark">{t('publicTryOn.ctaCreate')}</Link>
                <Link to={celebrityPath()} className="btn btn-light">{t('publicTryOn.ctaFind')}</Link>
              </div>
            </section>

            {tryOn.try_on_type === 'selected_products' && tryOn.selected_products?.length ? (
              <div className="ct-public-selected-products ct-user-fit-products">
                <strong>{t('tryon.selectedProductsTitle', { total: formatSelectedTotal() })}</strong>
                {tryOn.selected_products.map((product) => {
                  const content = (
                    <>
                      {product.image_url ? <img src={productImageUrl(product.image_url)} alt="" /> : <i className="ph ph-shopping-bag"></i>}
                      <span>
                        <b>{product.name}</b>
                        <small>{product.store || product.brand || product.item_category}</small>
                      </span>
                      <em>{formatPrice(product)}</em>
                    </>
                  );
                  const href = product.affiliate_url || product.product_url;
                  return href ? (
                    <a href={href} target="_blank" rel="noreferrer" key={product.candidate_id}>
                      {content}
                    </a>
                  ) : (
                    <span key={product.candidate_id}>{content}</span>
                  );
                })}
                <p>{t('tryon.shopResponsibleCopy')}</p>
              </div>
            ) : null}
          </aside>
        </article>
      </section>
    </main>
  );
}
